import { Base, withMountWhenInView } from '@studiometa/js-toolkit';
import { damp, matrix, nextFrame } from '@studiometa/js-toolkit/utils';

import Cursor from './Cursor/Cursor.js';

/**
 * @typedef {object} ParallaxHeroRef
 * @property {HTMLElement} target
 * @property {HTMLImageElement} grayscale
 */

/**
 * @typedef {object} ParallaxHeroInterface
 * @property {ParallaxHeroRef} $refs
 */

/**
 * ParallaxHero class.
 */
export default class ParallaxHero extends withMountWhenInView(Base, { rootMargin: '50%' }) {
  /**
   * Config.
   */
  static config = {
    refs: ['target', 'grayscale'],
    options: {
      ratio: {
        type: Number,
        default: 0.1,
      },
      dampFactor: {
        type: Number,
        default: 0.1,
      },
    },
    emits: ['item-enter', 'item-leave'],
    components: {
      Cursor,
    },
  };

  /**
   * Translate Y.
   * @type {number}
   */
  translateY = 0;

  /**
   * Scroll progress Y.
   * @type {number}
   */
  progressY = 0;

  /**
   * Transform values.
   * @type {object}
   */
  transform = {
    translateY: 0,
  };

  /**
   * Mouseenter
   * @param {Event} event
   */
  onMouseenter(event) {
    event.stopPropagation();
    this.$emit('item-enter', this.$el);
  }

  /**
   * Mouseleave
   * @param {Event} event
   */
  onMouseleave(event) {
    event.stopPropagation();
    this.$emit('item-leave', this.$el);
  }

  /**
   * Update delta scroll on scroll.
   * @param   {import('@studiometa/js-toolkit/services/scroll').ScrollServiceProps} props
   * @this    {ParallaxHero & ParallaxHeroInterface}
   * @returns {void}
   */
  scrolled({ y }) {
    this.progressY = (y + window.innerHeight) / window.innerHeight - 1;
  }

  /**
   * Update values on raf.
   * @this    {ParallaxHero & ParallaxHeroInterface}
   * @returns {void}
   */
  ticked() {
    this.draw();
  }

  /**
   * Draw function.
   * @this    {ParallaxHero & ParallaxHeroInterface}
   * @returns {void}
   */
  draw() {
    this.translateY = this.progressY * this.$options.ratio;
    this.transform.translateY = damp(
      this.translateY,
      this.transform.translateY,
      this.$options.dampFactor
    );

    // Set cursor offset
    this.$children.Cursor.forEach((el) => {
      el.offsetY = -this.translateY;
    });

    // Defer DOM update to the next frame
    nextFrame(() => {
      this.$refs.target.style.transform = `${matrix(this.transform)} translateZ(0px)`;
    });
  }
}
